<template>
  <span v-if="workingReward">
    <v-row class="mt-8">
      <v-col cols="12">
        <v-select v-if="level>0" label="Tiers" outlined dense :items="[1,2,3]" v-model="tiers" @change="onNewTiers"
                  ref="tiers"/>
      </v-col>
    </v-row>

    <v-row class="mt-6" v-if="shouldInterestNonQualifyingRateBeShown()">
      <v-col cols="12">
        <v-text-field
            label="Non-qualifying rate"
            v-model="workingReward.nonQualifyingApr"
            type="number"
            hint="Ex. 0.75"
            suffix="%"
            hide-spin-buttons
            dense
            :rules="requiredPositiveAndBelowAllTiersRule"
            @input="onChange"
            name="nonQualifyingApr"
            ref="nonQualifyingApr"
        />
      </v-col>
    </v-row>

    <v-row class="mt-6">
      <v-col cols="12">
        <v-text-field
            v-if="level == 0"
            label="Non-qualifying rate"
            v-model="workingReward.tier1.apr"
            type="number"
            hint="Ex. 0.75"
            suffix="%"
            hide-spin-buttons
            dense
            :rules="requiredPositiveAndBelowAllTiersRule"
            @input="onChange"
            name="tier1apr"
        />
        <v-text-field
            v-else
            label="Tier 1 rate"
            v-model="workingReward.tier1.apr"
            type="number"
            hint="Ex. 0.75"
            suffix="%"
            hide-spin-buttons
            dense
            :rules="requiredPositiveAndAboveNonQualifyingRule"
            @input="onChange"
            name="tier1apr"
        />
      </v-col>
    </v-row>

      <template v-if="tiers > 1">

        <v-row class="mt-6">
          <v-col cols="12">
            <v-text-field
                label="Tier 1 cap"
                v-model="workingReward.tier1.cap"
                type="number"
                hint="Ex. 15000.00"
                prefix="$"
                hide-spin-buttons
                dense
                :rules="requiredAndPositiveRule"
                @input="onChange"
                name="tier1cap"
                ref="tier1apr"
            />
          </v-col>
        </v-row>

        <v-row class="mt-6"
               :style="{marginBottom: shouldInterestNonQualifyingRateBeShown() && workingReward.tier2.apr === workingReward.nonQualifyingApr ? '40px' : '24px'}">
          <v-col cols="12">
            <v-text-field
                label="Tier 2 rate"
                v-model="workingReward.tier2.apr"
                type="number"
                hint="Ex. 0.75"
                suffix="%"
                hide-spin-buttons
                dense
                :rules="requiredPositiveAndAboveNonQualifyingRule"
                @input="onChange"
                name="tier2apr"
                ref="tier2apr"
            />
          </v-col>
        </v-row>

          <template v-if="tiers > 2">

            <v-row class="mt-6">
              <v-col cols="12">
                <v-text-field
                    label="Tier 2 cap"
                    v-model="workingReward.tier2.cap"
                    type="number"
                    hint="Ex. 15000.00"
                    prefix="$"
                    hide-spin-buttons
                    dense
                    :rules="requiredAndPositiveRule"
                    @input="onChange"
                    name="tier2cap"
                    ref="tier2cap"
                />
              </v-col>
            </v-row>

            <v-row class="mt-6">
              <v-col cols="12">
                <v-text-field
                    label="Tier 3 rate"
                    v-model="workingReward.tier3.apr"
                    type="number"
                    hint="Ex. 0.75"
                    suffix="%"
                    hide-spin-buttons
                    dense
                    :rules="requiredPositiveAndAboveNonQualifyingRule"
                    @input="onChange"
                    name="tier3apr"
                    ref="tier3apr"
                />
              </v-col>
            </v-row>
        </template>

      </template>
  </span>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Prop, Watch,
} from 'vue-property-decorator';
import { InterestReward } from '@/api/product/levelrewards/rewards';
import { ValidationTool } from '@/util';

@Component
export default class InterestRewardEditor extends Vue {
  @Prop()
  value!: InterestReward;

  @Prop()
  level!: number;

  @Prop()
  productHasInterestReward!: boolean;

  @Prop()
  editingExistingReward!: boolean;

  @Prop()
  baseLevelRewardsPct!: number | null;

  @Prop()
  lowestRewardsPct!: number | null;

  workingReward: InterestReward | null = null;

  tiers: number = 1;

  requiredAndPositiveRule = [ValidationTool.requiredAndPositiveRuleFunction];

  requiredPositiveAndBelowAllTiersRule: Array<any> = [ValidationTool.requiredAndPositiveRuleFunction, this.nonQualifyingRateMustBeLowerThanAllTierRates];

  requiredPositiveAndAboveNonQualifyingRule: Array<any> = [ValidationTool.requiredAndPositiveRuleFunction, this.tieredRateMustBeAboveNonQualifyingRate];

  mounted() {
    this.initialize();
  }

  @Watch('value')
  initialize() {
    this.workingReward = this.value.clone() as InterestReward;

    if (this.workingReward.tier3) {
      this.tiers = 3;
    } else if (this.workingReward.tier2) {
      this.tiers = 2;
    } else {
      this.tiers = 1;
    }
  }

  onChange() {
    this.$emit('input', this.workingReward);
  }

  shouldInterestNonQualifyingRateBeShown(): boolean {
    // This is not an edit of an existing reward, only show if there are no existing Interest Rewards
    // on any of the levels.
    return !this.editingExistingReward && !this.productHasInterestReward;
  }

  nonQualifyingRateMustBeLowerThanAllTierRates(value: number | string): string | boolean {
    const rates = [this.workingReward!.tier1?.apr, this.workingReward!.tier2?.apr, this.workingReward!.tier3?.apr]
        .filter((apr: any) => apr !== undefined && apr !== null && apr !== '')
        .map((apr: any) => Number(apr))
        .filter((val: number) => !Number.isNaN(val) && Number.isFinite(val) && val >= 0);

    /* Nothing to compare to. */
    if (rates.length == 0) {
      return true;
    }

    let lowestRate = Number(this.lowestRewardsPct);

    if (this.shouldInterestNonQualifyingRateBeShown()) {
      lowestRate = Math.min(...rates);
    }

    const nonQualRate = Number(value);

    return ValidationTool.nonQualifyingRateMustBeLowerThanAnyTierRateRuleFunction(nonQualRate, lowestRate);
  }

  tieredRateMustBeAboveNonQualifyingRate(value: number | string): string | boolean {
    let nonQualRate = Number(this.baseLevelRewardsPct);
    const tierRate = Number(value);

    if (this.shouldInterestNonQualifyingRateBeShown()) {
      nonQualRate = Number(this.workingReward!.nonQualifyingApr);
    }

    return ValidationTool.tierRateMustBeHigherThanNonQualifyingRateRuleFunction(tierRate, nonQualRate);
  }

  onNewTiers() {
    if (this.tiers > 1 && !this.workingReward!.tier2) {
      this.workingReward!.tier2 = { apr: '' };
    }

    if (this.tiers === 3 && !this.workingReward!.tier3) {
      this.workingReward!.tier3 = {
        apr: '',
      };
    } else if (this.tiers === 2) {
      this.workingReward!.tier2!.cap = null;
      this.workingReward!.tier3 = null;
    } else {
      this.workingReward!.tier2 = null;
      this.workingReward!.tier3 = null;
      this.workingReward!.tier1.cap = null;
    }
    this.onChange();
  }
}
</script>

<style lang="scss" scoped>
.equal-rates-message-block {
  display: flex;
  width: 631px;
  padding: 16px !important;
  align-items: center;
  gap: 16px;
  border-radius: 4px;
  border: 1px solid var(--custom-warning, #FB8C00);
  background: var(--orange-lighten-5, #FFF3E0);
  text-align: center;
}

.error-message-row {
  margin-top: -4px;
}

.warning-text {
  margin-top: 2px;
  margin-left: 4px;
}
</style>
